import React, { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { Button } from '../../components/ui'
import Icon from '../../components/Icon'
import Select from '../../components/ui/Select'
import { formatReportPercent, formatReportRub } from '../../utils/formatRub'
import { reportPdfButtonStyle } from './reportPreviewUi'
import './ContractReportPreview.css'

function StageIncomeReportPreview() {
  const { id: idParam } = useParams()
  const id = idParam ? decodeURIComponent(idParam) : ''
  const navigate = useNavigate()
  const now = new Date()
  const currentYear = now.getFullYear()
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3)
  const [rows, setRows] = useState([])
  const [stageTitle, setStageTitle] = useState(id)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [downloadLoading, setDownloadLoading] = useState(false)
  const [periodType, setPeriodType] = useState('quarter')
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [selectedQuarter, setSelectedQuarter] = useState(currentQuarter)

  useEffect(() => {
    if (!id) return
    const loadTitle = async () => {
      try {
        const response = await api.get(`/company/stages/${encodeURIComponent(id)}/card`)
        setStageTitle(response.data?.stage?.display_name || response.data?.stage?.type || id)
      } catch (_) {
        setStageTitle(id)
      }
    }
    loadTitle()
  }, [id])

  useEffect(() => {
    if (!id) return
    const loadRows = async () => {
      setLoading(true)
      setError('')
      try {
        const params = { period: periodType, year: selectedYear }
        if (periodType === 'quarter') params.quarter = selectedQuarter
        const response = await api.get(`/company/stages/${encodeURIComponent(id)}/income-report`, { params })
        setRows(response.data?.rows || [])
      } catch (e) {
        setRows([])
        setError(e.response?.data?.error || 'Не удалось загрузить отчет по доходам')
      } finally {
        setLoading(false)
      }
    }
    loadRows()
  }, [id, periodType, selectedYear, selectedQuarter])

  const periodLabel = useMemo(() => {
    if (periodType === 'quarter') return `${selectedQuarter} кв. ${selectedYear} г.`
    return `Год ${selectedYear}`
  }, [periodType, selectedYear, selectedQuarter])

  const downloadFile = async (format) => {
    setDownloadLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ period: periodType, year: String(selectedYear) })
      if (periodType === 'quarter') params.set('quarter', String(selectedQuarter))
      const response = await api.get(`/company/stages/${encodeURIComponent(id)}/income-report/${format}?${params.toString()}`, { responseType: 'blob' })
      const blob = new Blob([response.data], { type: format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8' })
      const cd = response.headers['content-disposition']
      let filename = `otchet-po-dohodam-stadiya-${new Date().toISOString().slice(0, 10)}.${format}`
      if (cd) {
        const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i) || cd.match(/filename=["']?([^"';]+)["']?/i)
        if (m?.[1]) filename = m[1].trim()
      }
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = filename
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (e) {
      setError(e.response?.data?.error || `Не удалось скачать ${format.toUpperCase()}`)
    } finally {
      setDownloadLoading(false)
    }
  }

  const dateNow = new Date()
  const dateStr = dateNow.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
  const timeStr = dateNow.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })

  return (
    <div className="contract-report-preview">
      <div className="contract-report-preview__toolbar">
        <h1 className="contract-report-preview__toolbar-title">Отчет по доходам</h1>
        <div className="contract-report-preview__toolbar-actions">
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
            <input type="radio" name="stage-income-period" checked={periodType === 'quarter'} onChange={() => setPeriodType('quarter')} />
            <span>Квартал</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
            <input type="radio" name="stage-income-period" checked={periodType === 'year'} onChange={() => setPeriodType('year')} />
            <span>Год</span>
          </label>
          <Select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))} style={{ minWidth: '120px' }}>
            {Array.from({ length: 6 }, (_, i) => currentYear - i).map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
          {periodType === 'quarter' && (
            <Select value={selectedQuarter} onChange={(e) => setSelectedQuarter(Number(e.target.value))} style={{ minWidth: '120px' }}>
              <option value={1}>1 кв.</option>
              <option value={2}>2 кв.</option>
              <option value={3}>3 кв.</option>
              <option value={4}>4 кв.</option>
            </Select>
          )}
          <Button variant="ghost" onClick={() => navigate(`/stage/${encodeURIComponent(id)}`)} className="contract-report-preview__btn-back">← Назад</Button>
          <Button variant="secondary" onClick={() => downloadFile('csv')} disabled={downloadLoading}>
            <Icon name="download" size={18} /> Скачать CSV
          </Button>
          <Button variant="primary" onClick={() => downloadFile('pdf')} disabled={downloadLoading} style={reportPdfButtonStyle}>
            <Icon name="download" size={18} /> Скачать PDF
          </Button>
        </div>
      </div>

      {error && <div className="contract-report-preview__message contract-report-preview__message--error">{error}</div>}

      <div className="contract-report-preview__page pdf-page">
        <header className="contract-report-preview__header">
          <div className="contract-report-preview__header-left">
            <div className="contract-report-preview__logo">
              <img src="/logo/logo.png" alt="2ПБ" onError={e => { e.target.onerror = null; e.target.src = '/logo/logo.jpg' }} />
            </div>
            <div>
              <h2 className="contract-report-preview__title">ПРОЕКТНАЯ АРТЕЛЬ</h2>
              <p className="contract-report-preview__subtitle">Отчет по доходам стадии</p>
            </div>
          </div>
          <div className="contract-report-preview__header-right">
            <p className="contract-report-preview__date-label">Дата формирования</p>
            <p className="contract-report-preview__date">{dateStr} <span className="contract-report-preview__date-sep">|</span> {timeStr}</p>
          </div>
        </header>

        <section className="contract-report-preview__section">
          <div style={{ marginBottom: 'var(--spacing-md)', fontWeight: 600 }}>{stageTitle} · {periodLabel}</div>
          <div className="contract-report-preview__table-wrap">
            <table className="contract-report-preview__table">
              <thead>
                <tr>
                  <th>Команда</th>
                  <th>ФИО Сотрудника</th>
                  <th>Тип сотрудника</th>
                  <th>% пая</th>
                  <th>Сумма актов по сотруднику за выбранный период</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5}>Загрузка…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={5}>Нет данных для выбранного периода</td></tr>
                ) : rows.map((row, index) => (
                  <tr key={`${row.team_name}-${row.employee_name}-${index}`}>
                    <td>{row.team_name || 'Без команды'}</td>
                    <td>{row.employee_name || '—'}</td>
                    <td>{row.employee_type || '—'}</td>
                    <td className="contract-report-preview__num">{formatReportPercent(row.share_percent)}</td>
                    <td className="contract-report-preview__num">{formatReportRub(row.acts_sum)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  )
}

export default StageIncomeReportPreview
